/**
 * Generate Invite Modal
 * Modal for generating new invite codes
 */
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Code,
  IconButton,
  Tooltip,
  useToast,
  Box,
} from '@chakra-ui/react';
import { Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { inviteService, type Invite } from '../../services/InviteService';
import { useAuth } from '../../contexts/AuthContext';

interface GenerateInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GenerateInviteModal: React.FC<GenerateInviteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [generatedInvite, setGeneratedInvite] = useState<Invite | null>(null);
  const [remainingInvites, setRemainingInvites] = useState<number | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Handle generate
  const handleGenerate = async () => {
    try {
      setLoading(true);

      const response = await inviteService.generateInvite();

      setGeneratedInvite(response.invite);
      setRemainingInvites(response.remaining_invites);

      toast({
        title: 'Success',
        description: 'Invite code generated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to generate invite',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setGeneratedInvite(null);
    setRemainingInvites(null);
    setCopiedCode(false);
    setCopiedUrl(false);
    onClose();
    if (generatedInvite) {
      onSuccess();
    }
  };

  // Copy code
  const handleCopyCode = async () => {
    if (!generatedInvite) return;
    try {
      await navigator.clipboard.writeText(generatedInvite.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast({
        title: 'Copied',
        description: 'Invite code copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Copy URL
  const handleCopyUrl = async () => {
    if (!generatedInvite) return;
    const url = `${window.location.origin}/register?invite=${generatedInvite.code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
      toast({
        title: 'Copied',
        description: 'Invite URL copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy URL',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const canGenerate = user && (user.role === 'admin' || (user.invites_available && user.invites_available > 0));

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate Invite Code</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* User Info */}
            {!generatedInvite && (
              <>
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    Available Invites
                  </Text>
                  <Text fontSize="3xl" fontWeight="bold">
                    {user?.role === 'admin' ? '∞' : user?.invites_available || 0}
                  </Text>
                </VStack>

                {!canGenerate && (
                  <Box p={3} bg="orange.50" borderRadius="md">
                    <Text fontSize="sm" color="orange.700">
                      You don't have any invites available to generate.
                      <br />
                      Ask an administrator to grant you more invites.
                    </Text>
                  </Box>
                )}

                <Text fontSize="sm" color="gray.600">
                  Click "Generate" to create a new invite code that you can share with others.
                </Text>
              </>
            )}

            {/* Generated Invite */}
            {generatedInvite && (
              <>
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    Your New Invite Code
                  </Text>
                  <HStack spacing={2}>
                    <Code fontSize="lg" colorScheme="green" p={3} flex={1} textAlign="center">
                      {generatedInvite.code}
                    </Code>
                    <Tooltip label={copiedCode ? 'Copied!' : 'Copy code'}>
                      <IconButton
                        aria-label="Copy code"
                        icon={copiedCode ? <CheckCircle size={16} /> : <Copy size={16} />}
                        onClick={handleCopyCode}
                        colorScheme={copiedCode ? 'green' : 'blue'}
                      />
                    </Tooltip>
                  </HStack>
                </VStack>

                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    Invite URL
                  </Text>
                  <HStack spacing={2}>
                    <Code fontSize="xs" p={2} flex={1} isTruncated>
                      {generatedInvite.invite_url}
                    </Code>
                    <Tooltip label={copiedUrl ? 'Copied!' : 'Copy URL'}>
                      <IconButton
                        aria-label="Copy URL"
                        icon={copiedUrl ? <CheckCircle size={16} /> : <ExternalLink size={16} />}
                        onClick={handleCopyUrl}
                        size="sm"
                        colorScheme={copiedUrl ? 'green' : 'blue'}
                      />
                    </Tooltip>
                  </HStack>
                </VStack>

                {remainingInvites !== null && user?.role !== 'admin' && (
                  <Box p={3} bg="blue.50" borderRadius="md">
                    <Text fontSize="sm" color="blue.700">
                      <strong>Remaining invites:</strong> {remainingInvites}
                    </Text>
                  </Box>
                )}

                <Text fontSize="sm" color="gray.600">
                  Share this code or URL with someone to invite them to register.
                </Text>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          {!generatedInvite ? (
            <>
              <Button variant="ghost" mr={3} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleGenerate}
                isLoading={loading}
                isDisabled={!canGenerate}
              >
                Generate
              </Button>
            </>
          ) : (
            <Button colorScheme="blue" onClick={handleClose}>
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GenerateInviteModal;
